import React, { useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
    FileText,
    Download,
    X,
    Truck,
    Calendar,
    CheckCircle2,
    FileSpreadsheet
} from "lucide-react";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface OTReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    ot: any;
}

export const OTReportModal: React.FC<OTReportModalProps> = ({ isOpen, onClose, ot }) => {
    const reportRef = useRef<HTMLDivElement>(null);

    if (!ot) return null;

    const exportToPDF = () => {
        const doc = new jsPDF();

        // Header
        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text("VILOX MANTENIMIENTO", 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`ORDEN DE TRABAJO: ${ot.codigo_ot}`, 14, 30);
        doc.text(`Estado: ${ot.estado}`, 14, 35);
        doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 40);

        // Vehicle Info
        autoTable(doc, {
            startY: 45,
            head: [['Información del Vehículo', 'Detalle']],
            body: [
                ['Placa', ot.vehiculo?.placa || '-'],
                ['Marca/Modelo', `${ot.vehiculo?.marca || ''} ${ot.vehiculo?.modelo || ''}`],
                ['Kilometraje', `${ot.kilometraje_inicio || 0} KM`],
                ['Tipo de Mantenimiento', ot.tipo || '-'],
                ['Prioridad', ot.prioridad || '-'],
            ],
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229] }
        });

        // Activities & Parts
        const activitiesBody = ot.actividades?.map((act: any) => {
            const isExcluded = act.observaciones?.startsWith("Postergada") || act.observaciones?.startsWith("Cancelada");
            const price = isExcluded ? 0 : Number(act.precio_cotizado || act.costo_unitario || 0);
            const qty = isExcluded ? 0 : Number(act.cantidad || 0);

            let statusLabel = act.realizado ? 'REALIZADO' : 'PENDIENTE';
            if (act.observaciones?.startsWith("Cancelada")) statusLabel = 'CANCELADO';
            if (act.observaciones?.startsWith("Postergada")) statusLabel = 'POSTERGADO';

            return [
                act.item_catalogo?.nombre || act.nombre || 'N/A',
                isExcluded ? '-' : qty,
                act.item_catalogo?.unidad_medida || 'UND',
                isExcluded ? 'S/ 0.00' : `S/ ${Number(act.precio_cotizado || act.costo_unitario || 0).toFixed(2)}`,
                `S/ ${(price * qty).toFixed(2)}`,
                statusLabel
            ];
        }) || [];

        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 10,
            head: [['Actividad/Repuesto', 'Cant', 'Unid', 'P. Unit', 'Subtotal', 'Estado']],
            body: activitiesBody,
            headStyles: { fillColor: [51, 65, 85] }
        });

        // Total
        const totalParts = ot.actividades
            ?.filter((act: any) => !act.observaciones?.startsWith("Postergada") && !act.observaciones?.startsWith("Cancelada"))
            .reduce((acc: number, curr: any) =>
                acc + Number(curr.precio_cotizado || curr.costo_unitario || 0) * Number(curr.cantidad || 0), 0
            ) || 0;
        const totalManoObra = Number(ot.costo_mano_obra || 0);
        const total = totalParts + totalManoObra;

        doc.setFontSize(10);
        const tallerText = ot.taller ? `Taller: ${ot.taller.nombre}` : 'Taller: No asignado';
        const mecanicosText = ot.mecanicos?.length > 0 ? `Mecánicos: ${ot.mecanicos.map((m: any) => m.nombre).join(', ')}` : 'Mecánicos: No asignados';
        doc.text(tallerText, 14, (doc as any).lastAutoTable.finalY + 12);
        doc.text(mecanicosText, 14, (doc as any).lastAutoTable.finalY + 17);

        doc.setFontSize(12);
        doc.setTextColor(40);
        doc.setFontSize(12);
        doc.setTextColor(40);
        doc.text(`TOTAL ITEMS: ${ot.actividades?.filter((act: any) => !act.observaciones?.startsWith("Postergada") && !act.observaciones?.startsWith("Cancelada")).length || 0} (S/ ${totalParts.toFixed(2)})`, 14, (doc as any).lastAutoTable.finalY + 27);
        doc.text(`MANO DE OBRA: S/ ${totalManoObra.toFixed(2)}`, 14, (doc as any).lastAutoTable.finalY + 34);
        doc.text(`TOTAL ESTIMADO: S/ ${total.toFixed(2)}`, 14, (doc as any).lastAutoTable.finalY + 42);

        doc.save(`${ot.codigo_ot}_Reporte.pdf`);
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ficha OT');

        // Configure Columns
        worksheet.columns = [
            { header: 'DESCRIPCIÓN', key: 'desc', width: 45 },
            { header: 'CANTIDAD', key: 'qty', width: 12 },
            { header: 'UNIDAD', key: 'unit', width: 10 },
            { header: 'PRECIO UNIT.', key: 'price', width: 15 },
            { header: 'SUBTOTAL', key: 'subtotal', width: 15 },
            { header: 'ESTADO', key: 'status', width: 15 },
        ];

        // --- HEADER SECTION ---
        const titleRow = worksheet.addRow(['VILOX MANTENIMIENTO']);
        titleRow.font = { name: 'Arial Black', size: 16, color: { argb: 'FFFFFFFF' } };
        titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } }; // Slate-900
        worksheet.mergeCells(`A${titleRow.number}:F${titleRow.number}`);
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const subTitleRow = worksheet.addRow([`ORDEN DE TRABAJO - ${ot.codigo_ot}`]);
        subTitleRow.font = { bold: true, size: 12, color: { argb: 'FF4F46E5' } }; // Indigo-600
        worksheet.mergeCells(`A${subTitleRow.number}:F${subTitleRow.number}`);
        subTitleRow.getCell(1).alignment = { horizontal: 'center' };

        worksheet.addRow([]); // Spacer

        // --- INFO BLOCKS ---
        // Header Info
        const infoHeader = worksheet.addRow(['INFORMACIÓN GENERAL', '', 'DATOS DEL VEHÍCULO', '', '', '']);
        infoHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        infoHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }; // Slate-700
        infoHeader.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }; // Slate-700
        worksheet.mergeCells(`A${infoHeader.number}:B${infoHeader.number}`);
        worksheet.mergeCells(`C${infoHeader.number}:F${infoHeader.number}`);

        const row1 = worksheet.addRow(["Código OT:", ot.codigo_ot, "Placa:", ot.vehiculo?.placa || '-', "", ""]);
        const row2 = worksheet.addRow(["Estado:", ot.estado, "Marca/Modelo:", `${ot.vehiculo?.marca || ''} ${ot.vehiculo?.modelo || ''}`.trim() || '-', "", ""]);
        const row3 = worksheet.addRow(["Fecha Reporte:", new Date().toLocaleDateString(), "Kilometraje:", `${ot.kilometraje_inicio || 0} KM`, "", ""]);
        const row4 = worksheet.addRow(["Tipo de OT:", ot.tipo, "Prioridad:", ot.prioridad, "", ""]);
        const row5 = worksheet.addRow(["Taller:", ot.taller?.nombre || 'No asignado', "Mecánicos:", ot.mecanicos?.length > 0 ? ot.mecanicos.map((m: any) => m.nombre).join(', ') : 'No asignados', "", ""]);

        // Style labels in info blocks
        [row1, row2, row3, row4, row5].forEach(r => {
            r.getCell(1).font = { bold: true };
            r.getCell(3).font = { bold: true };
        });

        worksheet.addRow([]); // Spacer

        // --- ACTIVITIES TABLE ---
        const activitiesHeader = worksheet.addRow(["DETALLE DE ACTIVIDADES Y MATERIALES", "", "", "", "", ""]);
        activitiesHeader.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        activitiesHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } }; // Slate-950
        worksheet.mergeCells(`A${activitiesHeader.number}:F${activitiesHeader.number}`);

        const tableHeader = worksheet.addRow(["DESCRIPCIÓN", "CANTIDAD", "UNIDAD", "P. UNITARIO", "SUBTOTAL", "ESTADO"]);
        tableHeader.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FF475569' } }; // Slate-600
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }; // Slate-100
            cell.border = { bottom: { style: 'thin' } };
        });

        let totalAmount = 0;
        let totalManoObra = Number(ot.costo_mano_obra || 0);

        ot.actividades?.forEach((act: any) => {
            const isExcluded = act.observaciones?.startsWith("Postergada") || act.observaciones?.startsWith("Cancelada");
            const price = isExcluded ? 0 : Number(act.precio_cotizado || act.costo_unitario || 0);
            const qty = isExcluded ? 0 : Number(act.cantidad || 0);
            const subtotal = price * qty;
            totalAmount += subtotal;

            let statusLabel = act.realizado ? 'REALIZADO' : 'PENDIENTE';
            if (act.observaciones?.startsWith("Cancelada")) statusLabel = 'CANCELADO';
            if (act.observaciones?.startsWith("Postergada")) statusLabel = 'POSTERGADO';

            const r = worksheet.addRow([
                act.item_catalogo?.nombre || act.nombre || 'N/A',
                isExcluded ? 0 : qty,
                act.item_catalogo?.unidad_medida || 'UND',
                isExcluded ? 0 : Number(act.precio_cotizado || act.costo_unitario || 0),
                subtotal,
                statusLabel
            ]);

            // Add currency format
            r.getCell(4).numFmt = '"S/" #,##0.00';
            r.getCell(5).numFmt = '"S/" #,##0.00';

            // Color for status
            if (act.realizado) {
                r.getCell(6).font = { color: { argb: 'FF059669' }, bold: true }; // Emerald-600
            } else if (isExcluded) {
                r.getCell(6).font = { color: { argb: 'FFEF4444' }, bold: true }; // Red-500
            } else {
                r.getCell(6).font = { color: { argb: 'FFD97706' }, bold: true }; // Amber-600
            }
        });

        // Add Mano de Obra
        const rowManoObra = worksheet.addRow([
            'MANO DE OBRA',
            '-',
            '-',
            totalManoObra,
            totalManoObra,
            'REALIZADO'
        ]);
        rowManoObra.getCell(4).numFmt = '"S/" #,##0.00';
        rowManoObra.getCell(5).numFmt = '"S/" #,##0.00';
        rowManoObra.getCell(6).font = { color: { argb: 'FF059669' }, bold: true };
        totalAmount += totalManoObra;

        // --- FOOTER ---
        worksheet.addRow([]);
        const subtotalItemsPrice = totalAmount - totalManoObra;
        const totalItemsRow = worksheet.addRow(["", "Items Totales (Ejecutados):", ot.actividades?.filter((a: any) => a.realizado).length || 0, "Subtotal Items:", subtotalItemsPrice]);
        totalItemsRow.getCell(2).font = { bold: true };
        totalItemsRow.getCell(4).font = { bold: true };
        totalItemsRow.getCell(5).numFmt = '"S/" #,##0.00';

        const rowManoObraExcel = worksheet.addRow(["", "", "", "Mano de Obra:", totalManoObra]);
        rowManoObraExcel.getCell(4).font = { bold: true };
        rowManoObraExcel.getCell(5).numFmt = '"S/" #,##0.00';

        const totalRow = worksheet.addRow(["", "", "", "VALOR TOTAL:", totalAmount]);
        totalRow.getCell(4).font = { bold: true, size: 12 };
        totalRow.getCell(5).font = { bold: true, size: 14, color: { argb: 'FF4F46E5' } };
        totalRow.getCell(5).numFmt = '"S/" #,##0.00';

        worksheet.addRow([]);
        const generatedRow = worksheet.addRow(["GENERADO POR VILOX SYSTEM", "", "", "", "", new Date().toLocaleString()]);
        generatedRow.font = { italic: true, size: 8, color: { argb: 'FF94A3B8' } };

        // Buffer and Save
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${ot.codigo_ot}_Reporte_Vilox.xlsx`);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-none shadow-2xl">
                <div className="bg-slate-900 p-6 flex justify-between items-center text-white sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-500 rounded-lg">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-black uppercase tracking-widest">
                                Vista Previa del Reporte
                            </DialogTitle>
                            <DialogDescription className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                Documento Oficial de Mantenimiento
                            </DialogDescription>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-8 bg-slate-50">
                    {/* El Reporte Visual (A4 simulado) */}
                    <div
                        ref={reportRef}
                        className="bg-white shadow-xl mx-auto p-12 border border-slate-200 min-h-[1000px] w-full max-w-[800px] flex flex-col gap-10"
                    >
                        {/* Header Reporte */}
                        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-8">
                            <div>
                                <h1 className="text-4xl font-black text-slate-900 tracking-tighter">VILOX</h1>
                                <p className="text-xs font-black text-indigo-600 uppercase tracking-[0.3em]">Mantenimiento</p>
                            </div>
                            <div className="text-right">
                                <div className="inline-block bg-slate-900 text-white px-4 py-2 mb-4">
                                    <span className="text-xs font-black uppercase tracking-widest">Orden de Trabajo</span>
                                </div>
                                <h2 className="text-3xl font-black text-slate-900">{ot.codigo_ot}</h2>
                                <p className="text-xs font-bold text-slate-500 uppercase mt-2">Estado: <span className={ot.estado === 'CERRADA' ? 'text-emerald-600' : 'text-blue-600'}>{ot.estado}</span></p>
                            </div>
                        </div>

                        {/* Info Section */}
                        <div className="grid grid-cols-2 gap-12">
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                                    <Truck className="w-4 h-4 text-slate-400" />
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Vehículo</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Placa</p>
                                        <p className="text-sm font-black text-slate-900">{ot.vehiculo?.placa}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Kilometraje</p>
                                        <p className="text-sm font-black text-slate-900">{ot.kilometraje_inicio?.toLocaleString()} KM</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Marca / Modelo</p>
                                        <p className="text-sm font-black text-slate-900">{ot.vehiculo?.marca} {ot.vehiculo?.modelo}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Programación</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Fecha Programada</p>
                                        <p className="text-sm font-black text-slate-900">{new Date(ot.fecha_programada).toLocaleDateString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Tipo OT</p>
                                        <p className={`text-sm font-black ${ot.tipo === 'CORRECTIVO' ? 'text-rose-600' : 'text-indigo-600'}`}>{ot.tipo}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">Prioridad</p>
                                        <p className="text-sm font-black text-slate-900">{ot.prioridad}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activities Table */}
                        <div className="flex-1">
                            <div className="flex items-center gap-2 border-b-2 border-slate-900 pb-2 mb-4">
                                <CheckCircle2 className="w-4 h-4 text-slate-900" />
                                <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Actividades y Materiales</h3>
                            </div>
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-900 border-b">Descripción</th>
                                        <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-900 border-b text-center">Cant</th>
                                        <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-900 border-b text-center">Unid</th>
                                        <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-900 border-b text-right">P. Unit</th>
                                        <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-900 border-b text-right">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {ot.actividades?.map((act: any, idx: number) => {
                                        const isExcluded = act.observaciones?.startsWith("Postergada") || act.observaciones?.startsWith("Cancelada");
                                        const price = isExcluded ? 0 : Number(act.precio_cotizado || act.costo_unitario || 0);
                                        const qty = isExcluded ? 0 : Number(act.cantidad || 0);

                                        return (
                                            <tr key={idx} className={`group italic ${isExcluded ? 'text-slate-400 bg-slate-50/50' : 'text-slate-700'}`}>
                                                <td className="px-4 py-4 text-sm font-bold capitalize">
                                                    {act.item_catalogo?.nombre || act.nombre}
                                                    {act.observaciones?.startsWith("Cancelada") && (
                                                        <span className="ml-2 text-[8px] bg-rose-100 text-rose-700 px-1 py-0.5 rounded uppercase font-black not-italic">Cancelado</span>
                                                    )}
                                                    {act.observaciones?.startsWith("Postergada") && (
                                                        <span className="ml-2 text-[8px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded uppercase font-black not-italic">Postergado</span>
                                                    )}
                                                    {!act.realizado && !isExcluded && (
                                                        <span className="ml-2 text-[8px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase font-black not-italic">Pendiente</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-center font-bold">{isExcluded ? '-' : qty}</td>
                                                <td className="px-4 py-4 text-sm text-center uppercase font-bold text-slate-400">{act.item_catalogo?.unidad_medida || 'UND'}</td>
                                                <td className="px-4 py-4 text-sm text-right font-bold italic">S/ {Number(act.precio_cotizado || act.costo_unitario || 0).toFixed(2)}</td>
                                                <td className="px-4 py-4 text-sm text-right font-black">S/ {(price * qty).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary Section */}
                        <div className="bg-slate-900 p-8 flex justify-between items-center mt-auto rounded-xl">
                            <div className="flex gap-8">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Items Totales</p>
                                    <p className="text-2xl font-black text-white">{ot.actividades?.filter((act: any) => !act.observaciones?.startsWith("Postergada") && !act.observaciones?.startsWith("Cancelada")).length || 0}</p>
                                    <p className="text-[9px] text-slate-500 mt-1 uppercase">S/ {(ot.actividades?.filter((act: any) => !act.observaciones?.startsWith("Postergada") && !act.observaciones?.startsWith("Cancelada")).reduce((acc: number, curr: any) => acc + (Number(curr.precio_cotizado || curr.costo_unitario || 0) * Number(curr.cantidad || 0)), 0) || 0).toFixed(2)}</p>
                                </div>
                                <div className="border-l border-white/10 pl-8">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Mano de Obra</p>
                                    <p className="text-2xl font-black text-white">S/ {Number(ot.costo_mano_obra || 0).toFixed(2)}</p>
                                    <p className="text-[9px] text-slate-500 mt-1 max-w-[150px] truncate" title={`${ot.taller ? ot.taller.nombre : 'Sin Taller'} | ${ot.mecanicos?.length ? ot.mecanicos.map((m: any) => m.nombre).join(', ') : 'Sin Mecánico'}`}>
                                        {ot.taller ? ot.taller.nombre : 'Sin Taller'} | {ot.mecanicos?.length ? ot.mecanicos.map((m: any) => m.nombre).join(', ') : 'Sin Mecánico'}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Monto Total Estimado</p>
                                <h3 className="text-4xl font-black text-white italic">
                                    S/ {(
                                        Number(ot.costo_mano_obra || 0) +
                                        (ot.actividades
                                            ?.filter((act: any) => !act.observaciones?.startsWith("Postergada") && !act.observaciones?.startsWith("Cancelada"))
                                            .reduce((acc: number, curr: any) =>
                                                acc + (Number(curr.precio_cotizado || curr.costo_unitario || 0) * Number(curr.cantidad || 0)), 0
                                            ) || 0)
                                    ).toFixed(2)}
                                </h3>
                            </div>
                        </div>

                        {/* Footer Signatures */}
                        <div className="mt-12 grid grid-cols-2 gap-20 pt-12">
                            <div className="border-t border-slate-200 text-center pt-4">
                                <p className="text-[10px] font-black uppercase text-slate-900">Firma Jefe de Taller / Mecánico</p>
                                <p className="text-[8px] text-slate-400 mt-1 uppercase tracking-tighter">Responsable de Ejecución</p>
                            </div>
                            <div className="border-t border-slate-200 text-center pt-4">
                                <p className="text-[10px] font-black uppercase text-slate-900">Firma Administración Flota</p>
                                <p className="text-[8px] text-slate-400 mt-1 uppercase tracking-tighter">Validación y Cierre de Costos</p>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="p-6 bg-white border-t flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex gap-2">
                        <Button
                            variant="outline"
                            className="flex-1 h-12 border-2 border-slate-200 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all rounded-xl"
                            onClick={exportToExcel}
                        >
                            <FileSpreadsheet className="w-5 h-5 mr-2 text-emerald-600" />
                            Excel Detallado
                        </Button>
                        <Button
                            className="flex-1 h-12 bg-slate-900 hover:bg-black text-white font-black text-xs uppercase tracking-widest shadow-xl transition-all hover:scale-[1.02] rounded-xl border-none"
                            onClick={exportToPDF}
                        >
                            <Download className="w-5 h-5 mr-2" />
                            Descargar PDF
                        </Button>
                    </div>
                    <Button
                        variant="ghost"
                        className="h-12 px-8 font-black text-xs uppercase tracking-widest text-slate-400 hover:text-slate-900 rounded-xl"
                        onClick={onClose}
                    >
                        Cerrar Previsualización
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
